import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import {
  CreatePropertyInput, PROPERTY_TYPES, PropertyDetail, PropertyType, UpdatePropertyInput, realEstateService,
} from '@/services/real-estate.service';
import { ContactPicker, PickedContact, PROPERTY_TYPE_LABEL } from './shared';
import {
  UnitDraft, UnitErrors, UnitFields, defaultUnitType, draftRecap, emptyUnitDraft, isWebLink, splitLinks, splitList,
  unitCreatePayload, validateUnitDraft,
} from './UnitFields';

interface PropertyDraft {
  name: string;
  type: PropertyType;
  community: string;
  city: string;
  address: string;
  owner: PickedContact | null;
  yearBuilt: string;
  description: string;
  amenities: string;
  imageUrls: string;
  latitude: string;
  longitude: string;
}
type PropertyErrors = Partial<Record<keyof PropertyDraft, string>>;
interface UnitRow { key: string; draft: UnitDraft; open: boolean; errors: UnitErrors }

const EMPTY: PropertyDraft = {
  name: '', type: 'building', community: '', city: '', address: '', owner: null, yearBuilt: '',
  description: '', amenities: '', imageUrls: '', latitude: '', longitude: '',
};

const numText = (v?: number | null) => (v === null || v === undefined ? '' : String(v));

function draftFromProperty(p: PropertyDetail): PropertyDraft {
  return {
    name: p.name,
    type: p.type,
    community: p.community ?? '',
    city: p.city ?? '',
    address: p.address ?? '',
    owner: p.ownerContactUid ? { contactUid: p.ownerContactUid, name: p.ownerName || 'Owner' } : null,
    yearBuilt: numText(p.yearBuilt),
    description: p.description ?? '',
    amenities: (p.amenities ?? []).join(', '),
    imageUrls: (p.imageUrls ?? []).join('\n'),
    latitude: numText(p.latitude),
    longitude: numText(p.longitude),
  };
}

const parseNum = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

function validateProperty(d: PropertyDraft): PropertyErrors {
  const e: PropertyErrors = {};
  if (!d.name.trim()) e.name = 'Name the property';
  const year = parseNum(d.yearBuilt);
  if (year !== null && (Number.isNaN(year) || !Number.isInteger(year) || year < 1800 || year > 2100)) e.yearBuilt = 'A year from 1800 to 2100';
  const lat = parseNum(d.latitude);
  if (lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) e.latitude = 'From -90 to 90';
  const lng = parseNum(d.longitude);
  if (lng !== null && (Number.isNaN(lng) || lng < -180 || lng > 180)) e.longitude = 'From -180 to 180';
  const links = splitLinks(d.imageUrls);
  const bad = links.find((l) => !isWebLink(l));
  if (bad) e.imageUrls = `Not a web link: ${bad.slice(0, 60)}`;
  else if (links.length > 20) e.imageUrls = 'Up to 20 image links';
  if (splitList(d.amenities).length > 40) e.amenities = 'Up to 40 amenities';
  return e;
}

const numOrUndef = (s: string) => {
  const n = parseNum(s);
  return n === null || Number.isNaN(n) ? undefined : n;
};

function createBody(d: PropertyDraft, units: UnitRow[]): CreatePropertyInput {
  return {
    name: d.name.trim(),
    type: d.type,
    community: d.community.trim() || undefined,
    city: d.city.trim() || undefined,
    address: d.address.trim() || undefined,
    ownerContactUid: d.owner?.contactUid || undefined,
    yearBuilt: numOrUndef(d.yearBuilt),
    description: d.description.trim() || undefined,
    amenities: splitList(d.amenities),
    imageUrls: splitLinks(d.imageUrls),
    latitude: numOrUndef(d.latitude),
    longitude: numOrUndef(d.longitude),
    units: units.map((u) => unitCreatePayload(u.draft)),
  };
}

/** Only what changed. Cleared numbers go as null, cleared text as ''. */
function patchBody(d: PropertyDraft, was: PropertyDraft): UpdatePropertyInput {
  const p: UpdatePropertyInput = {};
  const text = (k: 'name' | 'community' | 'city' | 'address' | 'description') => d[k].trim() !== was[k].trim();
  if (text('name')) p.name = d.name.trim();
  if (text('community')) p.community = d.community.trim();
  if (text('city')) p.city = d.city.trim();
  if (text('address')) p.address = d.address.trim();
  if (text('description')) p.description = d.description.trim();
  if (d.type !== was.type) p.type = d.type;
  const num = (k: 'yearBuilt' | 'latitude' | 'longitude') => {
    if (d[k].trim() === was[k].trim()) return;
    const n = parseNum(d[k]);
    p[k] = n === null || Number.isNaN(n) ? null : n;
  };
  num('yearBuilt'); num('latitude'); num('longitude');
  if ((d.owner?.contactUid ?? '') !== (was.owner?.contactUid ?? '')) p.ownerContactUid = d.owner?.contactUid ?? '';
  if (splitList(d.amenities).join('\n') !== splitList(was.amenities).join('\n')) p.amenities = splitList(d.amenities);
  if (splitLinks(d.imageUrls).join('\n') !== splitLinks(was.imageUrls).join('\n')) p.imageUrls = splitLinks(d.imageUrls);
  return p;
}

let rowSeq = 0;
const newRow = (type: PropertyType): UnitRow => ({ key: `u${++rowSeq}`, draft: emptyUnitDraft({ unitType: defaultUnitType(type) }), open: true, errors: {} });

interface Props {
  open: boolean;
  /** Edit this property; without it the modal creates one, with its units. */
  property?: PropertyDetail | null;
  onClose: () => void;
  onSaved: (p: PropertyDetail) => void;
}

export default function PropertyFormModal({ open, property, onClose, onSaved }: Props) {
  const t = useLabels();
  const currency = useCompanyCurrency();
  const editing = !!property;
  const noun = t('properties', 'singular');
  const [draft, setDraft] = useState<PropertyDraft>(EMPTY);
  const initial = useRef<PropertyDraft>(EMPTY);
  const [errors, setErrors] = useState<PropertyErrors>({});
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [showLocation, setShowLocation] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const d = property ? draftFromProperty(property) : EMPTY;
    initial.current = d;
    setDraft(d);
    setErrors({});
    setUnits(property ? [] : [newRow(d.type)]);
    setShowLocation(!!(d.latitude || d.longitude));
  }, [open, property]);

  const set = (patch: Partial<PropertyDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const changeType = (type: PropertyType) => {
    const was = defaultUnitType(draft.type);
    const next = defaultUnitType(type);
    set({ type });
    // Units still on the old default follow the new property type; ones the user picked stay.
    setUnits((rows) => rows.map((r) => (r.draft.unitType === was ? { ...r, draft: { ...r.draft, unitType: next } } : r)));
  };

  const patchUnit = (key: string, patch: Partial<UnitDraft>) =>
    setUnits((rows) => rows.map((r) => (r.key === key ? { ...r, draft: { ...r.draft, ...patch }, errors: {} } : r)));
  const toggleUnit = (key: string) => setUnits((rows) => rows.map((r) => (r.key === key ? { ...r, open: !r.open } : r)));
  const removeUnit = (key: string) => setUnits((rows) => rows.filter((r) => r.key !== key));
  const addUnit = () => setUnits((rows) => [...rows.map((r) => ({ ...r, open: false })), newRow(draft.type)]);

  const save = async () => {
    const e = validateProperty(draft);
    const checked = units.map((r) => {
      const ue = validateUnitDraft(r.draft);
      return { ...r, errors: ue, open: r.open || Object.keys(ue).length > 0 };
    });
    setErrors(e);
    setUnits(checked);
    const firstUnitError = checked.map((r) => Object.values(r.errors)[0]).find(Boolean);
    const first = Object.values(e)[0] ?? firstUnitError;
    if (first) { toast.error(first); return; }
    if (units.length > 50) { toast.error('Up to 50 units at a time — add the rest from the property page'); return; }

    setBusy(true);
    try {
      if (property) {
        const body = patchBody(draft, initial.current);
        if (Object.keys(body).length === 0) { onClose(); return; }
        const saved = await realEstateService.updateProperty(property.propertyUid, body);
        toast.success(`${saved.name} updated`);
        onSaved(saved);
      } else {
        const saved = await realEstateService.createProperty(createBody(draft, units));
        const n = saved.units?.length ?? 0;
        toast.success(`${saved.name} added${n ? ` with ${n} ${n === 1 ? 'unit' : 'units'}` : ''}`);
        onSaved(saved);
      }
    } catch (err: unknown) {
      if (!(err as { response?: unknown })?.response) toast.error('Could not save — check your connection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={editing ? `Edit ${noun.toLowerCase()}` : `New ${noun.toLowerCase()}`} widthClass="max-w-3xl">
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FormField label="Name" required error={errors.name}>
          <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Marina Heights Tower, Villa 12 Palm Jumeirah" maxLength={140} invalid={!!errors.name} autoFocus />
        </FormField>
        <FormField label="Type">
          <Select value={draft.type} onChange={(e) => changeType(e.target.value as PropertyType)}>
            {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{PROPERTY_TYPE_LABEL[p]}</option>)}
          </Select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <FormField label="Community / area" hint="What customers ask for">
          <Input value={draft.community} onChange={(e) => set({ community: e.target.value })} placeholder="e.g. Dubai Marina, DHA Phase 6" maxLength={120} />
        </FormField>
        <FormField label="City">
          <Input value={draft.city} onChange={(e) => set({ city: e.target.value })} placeholder="e.g. Dubai" maxLength={120} />
        </FormField>
      </div>
      <FormField label="Address">
        <Input value={draft.address} onChange={(e) => set({ address: e.target.value })} maxLength={400} />
      </FormField>
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FormField label="Owner / landlord" hint={`One of your ${t('contacts').toLowerCase()}`}>
          <ContactPicker value={draft.owner} onChange={(owner) => set({ owner })} placeholder="Find the owner by name or phone" />
        </FormField>
        <FormField label="Year built" error={errors.yearBuilt}>
          <Input type="number" inputMode="numeric" min={1800} max={2100} value={draft.yearBuilt} onChange={(e) => set({ yearBuilt: e.target.value })} invalid={!!errors.yearBuilt} />
        </FormField>
      </div>
      <FormField label="Amenities" hint="Separate with commas" error={errors.amenities}>
        <Input value={draft.amenities} onChange={(e) => set({ amenities: e.target.value })} placeholder="Pool, Gym, Covered parking, 24/7 security" />
      </FormField>
      <FormField label="Description">
        <Textarea rows={2} maxLength={4000} value={draft.description} onChange={(e) => set({ description: e.target.value })} />
      </FormField>
      <FormField label="Photo links" hint="One link per line" error={errors.imageUrls}>
        <Textarea rows={2} value={draft.imageUrls} onChange={(e) => set({ imageUrls: e.target.value })} placeholder="https://…" />
      </FormField>

      <button type="button" onClick={() => setShowLocation((s) => !s)} aria-expanded={showLocation} className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
        <ChevronDown size={14} className={clsx('transition-transform', showLocation && 'rotate-180')} />
        Map pin (latitude / longitude)
      </button>
      {showLocation && (
        <div className="grid grid-cols-2 gap-x-3">
          <FormField label="Latitude" error={errors.latitude}>
            <Input type="number" inputMode="decimal" step="any" value={draft.latitude} onChange={(e) => set({ latitude: e.target.value })} placeholder="25.0800" invalid={!!errors.latitude} />
          </FormField>
          <FormField label="Longitude" error={errors.longitude}>
            <Input type="number" inputMode="decimal" step="any" value={draft.longitude} onChange={(e) => set({ longitude: e.target.value })} placeholder="55.1400" invalid={!!errors.longitude} />
          </FormField>
        </div>
      )}

      {!editing && (
        <section className="mt-2 border-t border-border pt-4">
          <div className="mb-3 flex items-end justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-text-primary">Units {units.length > 0 && <span className="font-normal text-text-muted">({units.length})</span>}</h4>
              <p className="text-2xs text-text-muted">What you rent or sell. A villa is one property with one unit; a tower has many.</p>
            </div>
            <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={addUnit} disabled={units.length >= 50}>Add unit</Button>
          </div>
          {units.length === 0 && (
            <p className="rounded-xl border border-dashed border-border px-4 py-5 text-center text-xs text-text-muted">
              No units yet — you can add them later from the {noun.toLowerCase()} page.
            </p>
          )}
          <div className="space-y-3">
            {units.map((r, i) => {
              const errorCount = Object.keys(r.errors).length;
              return (
                <div key={r.key} className={clsx('rounded-2xl border', errorCount ? 'border-danger/40' : 'border-border')}>
                  <div className="flex items-center gap-2 px-4 py-3">
                    <button type="button" onClick={() => toggleUnit(r.key)} aria-expanded={r.open} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                      <ChevronDown size={15} className={clsx('shrink-0 text-text-muted transition-transform', r.open && 'rotate-180')} />
                      <span className="truncate text-[13px] font-semibold text-text-primary">{r.draft.label.trim() || `Unit ${i + 1}`}</span>
                      <span className="hidden truncate text-2xs text-text-muted sm:inline">{draftRecap(r.draft)}</span>
                      {errorCount > 0 && <span className="shrink-0 rounded-full bg-danger-soft px-2 py-0.5 text-2xs font-semibold text-danger">Check {errorCount === 1 ? 'a field' : `${errorCount} fields`}</span>}
                    </button>
                    <button type="button" onClick={() => removeUnit(r.key)} aria-label={`Remove ${r.draft.label.trim() || `unit ${i + 1}`}`} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {r.open && (
                    <div className="border-t border-border px-4 pt-4">
                      <UnitFields draft={r.draft} onChange={(patch) => patchUnit(r.key, patch)} errors={r.errors} currency={currency} compact />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => void save()} loading={busy}>{editing ? 'Save' : `Create ${noun.toLowerCase()}`}</Button>
      </div>
    </Modal>
  );
}
